import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { calibracionApi, calibracionKeys } from '../calibracion.api';
import type {
  CodigoFiltros,
  DetalleReporteFiltros,
  EstadoCodigo,
  NuevoCodigoPayload,
  ReporteFiltros,
} from '../calibracion.types';

/** Códigos de calibración con filtros de estado y código. */
export function useCodigosCalibracion(filtros: CodigoFiltros, enabled = true) {
  return useQuery({
    queryKey: calibracionKeys.codigos(filtros),
    queryFn: ({ signal }) => calibracionApi.listarCodigos(filtros, signal),
    enabled,
    staleTime: 30_000,
  });
}

/** Totales por estado de los códigos. */
export function useCalibracionStats(enabled = true) {
  return useQuery({
    queryKey: calibracionKeys.stats,
    queryFn: ({ signal }) => calibracionApi.estadisticas(signal),
    enabled,
    staleTime: 30_000,
  });
}

/** Detalle de un código con su historial de uso. */
export function useDetalleCodigo(id: number | null, enabled = true) {
  return useQuery({
    queryKey: calibracionKeys.detalleCodigo(id ?? 0),
    queryFn: ({ signal }) => calibracionApi.detalleCodigo(id as number, signal),
    enabled: enabled && Boolean(id),
  });
}

/** Catálogos de los reportes (ejecutores, productos y lados). */
export function useCalibracionFiltros(enabled = true) {
  return useQuery({
    queryKey: calibracionKeys.filtros,
    queryFn: ({ signal }) => calibracionApi.filtros(signal),
    enabled,
    staleTime: 10 * 60_000,
  });
}

/** Reporte de calibraciones del período. */
export function useReporteCalibracion(filtros: ReporteFiltros, enabled = true) {
  return useQuery({
    queryKey: calibracionKeys.reporte(filtros),
    queryFn: ({ signal }) => calibracionApi.reporte(filtros, signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Reporte de detalles (una fila por bomba/manguera). */
export function useDetalleReporteCalibracion(filtros: DetalleReporteFiltros, enabled = true) {
  return useQuery({
    queryKey: calibracionKeys.detalles(filtros),
    queryFn: ({ signal }) => calibracionApi.detalles(filtros, signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Invalida todo el módulo (los totales y el listado dependen entre sí). */
function useInvalidarCalibracion() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: calibracionKeys.all });
  };
}

export function useCrearCodigoCalibracion() {
  const invalidar = useInvalidarCalibracion();

  return useMutation({
    mutationFn: (payload: NuevoCodigoPayload) => calibracionApi.crearCodigo(payload),
    onSuccess: () => invalidar(),
  });
}

export function useCambiarEstadoCodigo() {
  const invalidar = useInvalidarCalibracion();

  return useMutation({
    mutationFn: ({ id, estado }: { id: number; estado: EstadoCodigo }) =>
      calibracionApi.cambiarEstado(id, estado),
    onSuccess: () => invalidar(),
  });
}

export function useEliminarCodigo() {
  const invalidar = useInvalidarCalibracion();

  return useMutation({
    mutationFn: ({ id, forzar }: { id: number; forzar?: boolean }) =>
      calibracionApi.eliminarCodigo(id, forzar ?? false),
    onSuccess: () => invalidar(),
  });
}
